import React from "react";
import { message } from "antd";
import Axios from "axios";
import { connect } from "react-redux";
import InputText from "../orders/accepted_orders/InputFilter";
import Container from "../Layout/Container";
import ContentBlock from "../Layout/ContentBlock";
import FilterMenu from "../Layout/FilterMenu";
import TableFilterBox from "../common/TableFilterBox";
import ContentValidation from "../common/ContentValidation";
import { SelectForm } from "../common/Buttons";
import PaginationComp from "../Dashboard/items/Pagination";
import TableColumnAlerts from "./items/TableColumnAlerts";
import AlertEditDrawer from "./items/AlertEditDrawer";
import { ALERT_ROLES, NO_TEMPLATE, dayOptions, hourOptions } from "./alertHelpers";

const emptyFilter = {
  rule: "",
  title: "",
  role: "",
  days: "",
  hours: "",
  active: "",
};

class AlertsPage extends React.Component {
  constructor() {
    super();
    this.state = {
      page: 1,
      pageCount: 100,
      totals: 0,
      loading: false,
      paramsFilter: "",
      filter: { ...emptyFilter },
      data: [],
      rules: [],
      commentStates: [],
      drawerVisible: false,
      editedAlert: null,
    };
  }

  componentDidMount() {
    this.fetchRules();
    this.fetchCommentStates();
    this.reloadData();
  }

  componentDidUpdate(_, prevState) {
    if (
      prevState.page !== this.state.page ||
      prevState.pageCount !== this.state.pageCount ||
      prevState.paramsFilter !== this.state.paramsFilter
    ) {
      this.reloadData();
    }
  }

  fetchRules = () => {
    Axios.get("/api/v1/alerts/rules")
      .then((res) => {
        const rules = Object.entries(res.data.data || {}).map(([value, rule]) => ({
          value,
          label: rule.title,
          comment: rule.comment,
        }));
        this.setState({ rules });
      })
      .catch(() => {});
  };

  fetchCommentStates = () => {
    Axios.get("/api/v1/comment_states?per_page=500")
      .then((res) => {
        this.setState({ commentStates: res.data.data || [] });
      })
      .catch(() => {});
  };

  reloadData = () => {
    const { page, pageCount, paramsFilter } = this.state;
    this.setState({ loading: true });

    Axios.get(`/api/v1/alerts?page=${page}&per_page=${pageCount}${paramsFilter}`)
      .then((res) => {
        this.setState({
          data: res.data.data || [],
          totals: res.data.totals || 0,
          loading: false,
        });
      })
      .catch(() => {
        this.setState({ data: [], totals: 0, loading: false });
      });
  };

  onFilter = () => {
    const { rule, title, role, days, hours, active } = this.state.filter;
    const filterParams = [];

    if (rule === NO_TEMPLATE) {
      filterParams.push("&q[rule_blank]=1");
    } else if (rule) {
      filterParams.push(`&q[rule_eq]=${encodeURIComponent(rule)}`);
    }
    if (title.length > 0) {
      filterParams.push(`&q[title_cont]=${encodeURIComponent(title)}`);
    }
    if (role) filterParams.push(`&q[role_eq]=${encodeURIComponent(role)}`);
    if (days !== "" && days !== null) {
      filterParams.push(`&q[days_eq]=${encodeURIComponent(days)}`);
    }
    if (hours !== "" && hours !== null) {
      filterParams.push(`&q[hours_eq]=${encodeURIComponent(hours)}`);
    }
    if (active !== "" && active !== null) {
      filterParams.push(`&q[active_eq]=${encodeURIComponent(active)}`);
    }

    this.setState({ paramsFilter: filterParams.join(""), page: 1 });
  };

  filterChange = (e) => {
    this.setState({
      filter: { ...this.state.filter, [e.target.name]: e.target.value },
    });
  };

  selectFilterChange = (name, value) => {
    this.setState(
      { filter: { ...this.state.filter, [name]: value === undefined ? "" : value } },
      this.onFilter
    );
  };

  clearFilter = () => {
    this.setState({ filter: { ...emptyFilter }, paramsFilter: "", page: 1 });
  };

  handleKeyDown = (e) => {
    if (e.key === "Enter") this.onFilter();
  };

  onDelete = (id) => {
    Axios.delete(`/api/v1/alerts/${id}`)
      .then(() => {
        message.success("Alert deleted successfully");
        this.reloadData();
      })
      .catch((error) => {
        message.error(error?.response?.data?.message || "Failed to delete alert");
      });
  };

  openDrawer = (alert = null) => {
    this.setState({ drawerVisible: true, editedAlert: alert });
  };

  closeDrawer = () => {
    this.setState({ drawerVisible: false, editedAlert: null });
  };

  onSaved = () => {
    this.closeDrawer();
    this.reloadData();
  };

  setPagination = (page) => this.setState({ page });

  setPageCount = (pageCount) => this.setState({ pageCount, page: 1 });

  render() {
    const { data, page, pageCount, totals, loading, filter, rules, commentStates } =
      this.state;

    return (
      <Container
        location={{
          locationName: "Alert settings",
          locationUrl: this.props.match.url,
        }}
      >
        <>
          <ContentBlock>
            <FilterMenu
              totals={totals}
              length={data.length}
              onChangeCountPage={this.setPageCount}
              countPage={pageCount}
              text2="Alerts"
            />
          </ContentBlock>
          <ContentBlock>
            <TableFilterBox
              filter={false}
              onFilter={this.onFilter}
              clearFilter={this.clearFilter}
              button1={
                <button
                  type="button"
                  className="btn btn-sm btn-primary float-right mr-3"
                  onClick={() => this.openDrawer(null)}
                >
                  Create new
                </button>
              }
            >
              <div className="px-1">
                <InputText
                  width={"0.75rem"}
                  number="1"
                  name="title"
                  placeholder="Alert title"
                  onKeyDown={this.handleKeyDown}
                  value={filter.title}
                  onChange={this.filterChange}
                />
              </div>
              <div className="px-1" style={{ width: 260 }}>
                <SelectForm
                  placeholder="Alert rule"
                  onlyPlaceholder="All rules"
                  allowClear
                  options={[{ value: NO_TEMPLATE, label: "No template" }, ...rules]}
                  value={filter.rule}
                  onChange={(value) => this.selectFilterChange("rule", value)}
                />
              </div>
              <div className="px-1" style={{ width: 200 }}>
                <SelectForm
                  placeholder="Assigned role"
                  onlyPlaceholder="All roles"
                  allowClear
                  options={ALERT_ROLES}
                  value={filter.role}
                  onChange={(value) => this.selectFilterChange("role", value)}
                />
              </div>
              <div className="px-1" style={{ width: 150 }}>
                <SelectForm
                  placeholder="Trigger after, days"
                  onlyPlaceholder="Any days"
                  allowClear
                  options={dayOptions}
                  value={filter.days}
                  onChange={(value) => this.selectFilterChange("days", value)}
                />
              </div>
              <div className="px-1" style={{ width: 150 }}>
                <SelectForm
                  placeholder="Trigger after, hours"
                  onlyPlaceholder="Any hours"
                  allowClear
                  options={hourOptions}
                  value={filter.hours}
                  onChange={(value) => this.selectFilterChange("hours", value)}
                />
              </div>
              <div className="px-1" style={{ width: 140 }}>
                <SelectForm
                  placeholder="Active"
                  onlyPlaceholder="All"
                  allowClear
                  options={[
                    { value: "true", label: "Yes" },
                    { value: "false", label: "No" },
                  ]}
                  value={filter.active}
                  onChange={(value) => this.selectFilterChange("active", value)}
                />
              </div>
            </TableFilterBox>
            <div>
              <ContentValidation
                loader={loading}
                array={data}
                alertId_s="alertsAlert-s"
                alertId_e="alertsAlert-e"
              >
                <div className="table-responsive">
                  <table className="table table-sm table-hover mt-3">
                    <thead>
                      <tr className="text-muted">
                        <th className="text-black">#</th>
                        <th className="text-black">Alert title</th>
                        <th className="text-black">Alert rule</th>
                        <th className="text-black">Comment statuses</th>
                        <th className="text-black">Trigger after</th>
                        <th className="text-black">Assigned role</th>
                        <th className="text-black">Active</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((item, index) => (
                        <TableColumnAlerts
                          key={item.id}
                          index={index}
                          item={item}
                          page={page}
                          pageCount={pageCount}
                          rules={rules}
                          commentStates={commentStates}
                          onEdit={this.openDrawer}
                          onDelete={this.onDelete}
                        />
                      ))}
                    </tbody>
                  </table>
                  <PaginationComp
                    setPagination={this.setPagination}
                    page={page}
                    totals={totals}
                    pageCount={pageCount}
                  />
                </div>
              </ContentValidation>
            </div>
          </ContentBlock>

          <AlertEditDrawer
            visible={this.state.drawerVisible}
            alert={this.state.editedAlert}
            rules={this.state.rules}
            commentStates={this.state.commentStates}
            onClose={this.closeDrawer}
            onSaved={this.onSaved}
          />
        </>
      </Container>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: state.auth.user,
});

export default connect(mapStateToProps)(AlertsPage);
